import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Shield, 
  Eye,
  CheckCircle,
  XCircle,
  Key
} from 'lucide-react';
import { useAuth } from '@hooks/useAuth';
import { USER_ROLES } from '@constants';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import LoadingSpinner from '@components/common/LoadingSpinner';
import toast from 'react-hot-toast';
import api from '@services/api/api.service';
import CreateAdminModal from '@components/admin/CreateAdminModal';
import EditAdminModal from '@components/admin/EditAdminModal';

const AdminManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    if (user?.role !== USER_ROLES.ADMIN) {
      toast.error('Access denied. Admin only.');
      navigate('/admin');
      return;
    }
    if (!fetchError) {
      fetchAdmins();
    }
  }, [user, navigate, page, search, fetchError]);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      setFetchError(false);
      const response = await api.get('/admin-management', {
        params: { page, limit: 10, search }
      });
      
      if (response.data.success) {
        setAdmins(response.data.data);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch admins', error);
      setFetchError(true);
      
      const errorStatus = error.response?.status;
      if (errorStatus >= 500) {
        toast.error('Server error. Please try again later.');
      } else if (errorStatus === 403) {
        toast.error('You do not have permission to manage admins');
      } else if (errorStatus === 429) {
        toast.error('Too many requests. Please try again later.');
      } else if (!error.response) {
        toast.error('Cannot connect to server. Please check your connection.');
      } else {
        toast.error('Failed to fetch admins');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (adminId) => {
    if (!window.confirm('Are you sure you want to delete this admin?')) {
      return;
    }

    try {
      await api.delete(`/admin-management/${adminId}`);
      toast.success('Admin deleted successfully');
      fetchAdmins();
    } catch (error) {
      console.error('Failed to delete admin', error);
      toast.error(error.response?.data?.message || 'Failed to delete admin');
    }
  };

  const handleEdit = (admin) => {
    setSelectedAdmin(admin);
    setIsEditModalOpen(true);
  };

  const handleCreateSuccess = () => {
    setIsCreateModalOpen(false);
    fetchAdmins();
  };

  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
    setSelectedAdmin(null);
    fetchAdmins();
  };

  return (
    <div className="min-h-full bg-gray-50 p-3">
      <div className="mb-3 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-primary-900">
          Admin Management
        </h1>
        <Button
          variant="primary"
          onClick={() => setIsCreateModalOpen(true)}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create Admin
        </Button>
      </div>

      <Card className="p-4 mb-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-primary-400" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-white text-primary-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </div>
      </Card>

      <Card className="overflow-hidden">

        {loading ? (
          <div className="p-8 flex justify-center">
            <LoadingSpinner size="lg" />
          </div>
        ) : admins.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="w-12 h-12 text-primary-300 mx-auto mb-3" />
            <p className="text-primary-600">No admins found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Admin
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Permissions
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {admins.map((admin) => (
                    <tr key={admin.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white font-semibold">
                            {admin.firstName?.[0]?.toUpperCase() || admin.email?.[0]?.toUpperCase()}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                              {admin.firstName} {admin.lastName}
                              {admin.isSuperAdmin && (
                                <Shield className="w-4 h-4 text-yellow-500" title="Super Admin" />
                              )}
                            </div>
                            <div className="text-xs text-gray-500">
                              {admin.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{admin.email}</div>
                        <div className="text-xs text-gray-500">{admin.phone || 'N/A'}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {admin.isSuperAdmin ? (
                            <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold flex items-center gap-1">
                              <Key className="w-3 h-3" />
                              All Permissions
                            </span>
                          ) : (
                            <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                              {admin.permissions?.length || 0} Permissions
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {admin.isActive ? (
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 flex items-center gap-1 w-fit">
                            <CheckCircle className="w-3 h-3" />
                            Active
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 flex items-center gap-1 w-fit">
                            <XCircle className="w-3 h-3" />
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm">
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(admin)}
                            disabled={admin.isSuperAdmin}
                            title={admin.isSuperAdmin ? 'Cannot edit super admin' : 'Edit admin'}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(admin.id)}
                            disabled={admin.isSuperAdmin}
                            className="text-red-600 hover:text-red-700 hover:border-red-600"
                            title={admin.isSuperAdmin ? 'Cannot delete super admin' : 'Delete admin'}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination && pagination.totalPages > 1 && (
              <div className="px-3 py-2 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page === 1}
                    onClick={() => setPage(pagination.page - 1)}
                  >
                    Previous
                  </Button>
                  {[...Array(pagination.totalPages)].map((_, i) => (
                    <Button
                      key={i + 1}
                      variant={pagination.page === i + 1 ? 'primary' : 'outline'}
                      size="sm"
                      onClick={() => setPage(i + 1)}
                    >
                      {i + 1}
                    </Button>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page === pagination.totalPages}
                    onClick={() => setPage(pagination.page + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      <CreateAdminModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      {selectedAdmin && (
        <EditAdminModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedAdmin(null);
          }}
          admin={selectedAdmin}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
};

export default AdminManagement;
